import { useEffect, useRef } from 'react';
import { Camera, Clock, Upload, X, AlertTriangle, Loader2, RefreshCcw } from 'lucide-react';
import { FieldShell } from './FieldShell';
import { useFilePickerState } from './useFilePickerState';
import { useFormPendingUploads } from '../FormPendingUploadsContext';
import type { FieldComponentProps } from './types';

const IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png';

const detectMobile = (): { isIOS: boolean; isAndroid: boolean } => {
  if (typeof navigator === 'undefined') return { isIOS: false, isAndroid: false };
  const ua = navigator.userAgent || '';
  return {
    isIOS: /iPad|iPhone|iPod/i.test(ua),
    isAndroid: /Android/i.test(ua),
  };
};

export const VersionedImagePickerField = ({ field, value, error, onChange }: FieldComponentProps) => {
  const c = field.constraints || {};
  // Conservative defaults when the backend omits constraints: single file,
  // max 1. Multi-file requires explicit `allow_multiple: true` in metadata.
  const allowMultiple = c.allow_multiple === true;
  const maxFiles =
    typeof c.max_length === 'number' ? c.max_length : allowMultiple ? 10 : 1;
  const showCameraIOS = (c as any).show_camera_for_ios !== false;
  const showCameraAndroid = (c as any).show_camera_for_android !== false;
  const { isIOS, isAndroid } = detectMobile();
  const cameraVisible = (isIOS && showCameraIOS) || (isAndroid && showCameraAndroid);

  const uploadInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const rawDocTypeId = field.document_type_id;
  const parsedDocTypeId = Number(rawDocTypeId);
  const documentTypeId =
    Number.isFinite(parsedDocTypeId) && Number.isInteger(parsedDocTypeId) && parsedDocTypeId > 0
      ? parsedDocTypeId
      : undefined;
  const { entries, counts, canAddMore, addFiles, remove, retry, uploadPending, uploadDisabled } =
    useFilePickerState({
      value,
      onChange,
      maxFiles,
      allowMultiple,
      documentTypeId,
    });

  const pendingRegistry = useFormPendingUploads();
  const uploadPendingRef = useRef(uploadPending);
  uploadPendingRef.current = uploadPending;
  useEffect(() => {
    if (!pendingRegistry) return;
    return pendingRegistry.register(field.title, () => uploadPendingRef.current());
  }, [pendingRegistry, field.title]);

  const handleSelected = (files: FileList | null) => {
    if (!files) return;
    addFiles(files);
  };

  return (
    <FieldShell field={field} error={error}>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-rgf-gray-500">
            {counts.total} of {maxFiles} {allowMultiple ? '' : '(single only)'}
            {counts.staged > 0 && ` • ${counts.staged} pending upload`}
            {counts.uploading > 0 && ' • uploading…'}
            {counts.failed > 0 && ` • ${counts.failed} failed`}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm text-rgf-primary-700 hover:text-rgf-primary-800 disabled:opacity-40"
              onClick={() => uploadInputRef.current?.click()}
              disabled={uploadDisabled || !canAddMore}
              title={uploadDisabled ? 'Uploads are disabled in this context.' : undefined}
            >
              <Upload className="h-4 w-4" />
              Upload
            </button>
            {cameraVisible && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-sm text-rgf-primary-700 hover:text-rgf-primary-800 disabled:opacity-40"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploadDisabled || !canAddMore}
              >
                <Camera className="h-4 w-4" />
                Camera
              </button>
            )}
          </div>
        </div>

        <input
          ref={uploadInputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          multiple={allowMultiple}
          className="hidden"
          onChange={(e) => {
            handleSelected(e.target.files);
            e.target.value = '';
          }}
        />
        {cameraVisible && (
          <input
            ref={cameraInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            capture="environment"
            className="hidden"
            onChange={(e) => {
              handleSelected(e.target.files);
              e.target.value = '';
            }}
          />
        )}

        {uploadDisabled ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            File uploads are not configured for this form.
          </div>
        ) : null}

        {entries.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {entries.map((entry) => (
              <div
                key={entry.localKey}
                className={`relative rounded-lg border ${entry.error ? 'border-red-300' : 'border-rgf-gray-200'} bg-white overflow-hidden`}
              >
                {/* Prefer server-resolved URL when present, fall back to the
                    blob: URL of the staged file so the candidate sees a
                    thumbnail straight after attaching. */}
                {(() => {
                  const previewSrc = !entry.uploading && !entry.error
                    ? entry.resolvedUrl ?? entry.localPreviewUrl
                    : undefined;
                  return previewSrc ? (
                    <img
                      src={previewSrc}
                      alt={entry.filename || 'Selected image'}
                      className="block w-full h-28 object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-28 bg-rgf-gray-50">
                      {entry.uploading ? (
                        <Loader2 className="h-5 w-5 text-rgf-gray-400 animate-spin" />
                      ) : entry.error ? (
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                      ) : entry.staged ? (
                        <Clock className="h-5 w-5 text-amber-500" />
                      ) : (
                        <Loader2 className="h-5 w-5 text-rgf-gray-400 animate-spin" />
                      )}
                    </div>
                  );
                })()}
                <div className="px-2 py-1 text-xs truncate text-rgf-gray-600" title={entry.filename}>
                  {entry.filename || 'file'}
                </div>
                {entry.error && (
                  <div
                    className="px-2 pb-2 text-[11px] leading-snug text-red-600 break-words"
                    title={entry.error}
                  >
                    {entry.pending_submission_id
                      ? `${entry.error} (submission #${entry.pending_submission_id})`
                      : entry.error}
                  </div>
                )}
                <div className="absolute top-1 right-1 flex gap-1">
                  {entry.error && (
                    <button
                      type="button"
                      aria-label="Retry upload"
                      className="p-1 rounded-full bg-white text-rgf-gray-600 shadow hover:text-rgf-gray-900"
                      onClick={() => retry(entry.localKey)}
                    >
                      <RefreshCcw className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="Remove file"
                    className="p-1 rounded-full bg-white text-rgf-gray-600 shadow hover:text-red-600"
                    onClick={() => remove(entry.localKey)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </FieldShell>
  );
};
